<template>
  <app-content class="installation-edit">
    <s-loader
      v-show="loading"
      class="h-100"
      size="lg"
    />
    <b-container
      v-show="!loading"
      class="installation-edit__container"
      fluid
    >
      <b-row>
        <b-col>
          <label>
            {{ $t('fields.installation.installation-name') }}*
          </label>
          <b-form-input
            v-model="installation.name"
            maxlength="80"
            :disabled="isSaving"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label>
            {{ $t('fields.installation.installation-type') }}
          </label>
          <b-form-input
            v-model="installation.installationType"
            maxlength="150"
            :disabled="isSaving"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col
          xs="12"
          sm="10"
        >
          <label>
            {{ $t('fields.installation.complex') }}
          </label>
          <b-form-input
            v-model="installation.complex"
            maxlength="150"
            :disabled="isSaving"
          />
        </b-col>
        <b-col
          xs="12"
          sm="2"
        >
          <label>
            {{ $t('fields.installation.federative-unit') }}
          </label>
          <b-form-input
            v-model="installation.federativeUnit"
            maxlength="150"
            :disabled="isSaving"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <label>
            {{ $t('fields.installation.timezone') }}*
          </label>
          <s-icon
            name="hint"
            v-b-tooltip.hover.right
            :title="$t('messages.MI011.message')"
          />
          <s-select
            v-model="installation.timezone"
            value-field="id"
            text-field="name"
            :options="timeZones"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <fieldset class="installation-edit__coordinates-fieldset">
            <legend class="installation-edit__coordinates-legend">
              {{ $t('fields.installation.coordinates') }}
            </legend>
            <b-row class="mb-4">
              <b-col
                xs="12"
                sm="6"
                md="4"
              >
                <label>
                  {{ $t('fields.installation.latitude') }}*
                </label>
                <the-mask
                  v-model="installation.latitude"
                  :disabled="disableCoordinatesFields"
                  :mask="latitudeMask"
                  :tokens="maskPatterns"
                  :masked="true"
                  class="form-control"
                />

              </b-col>
              <b-col
                xs="12"
                sm="6"
                md="4"
              >
                <label>
                  {{ $t('fields.installation.longitude') }}*
                </label>
                <the-mask
                  v-model="installation.longitude"
                  :disabled="disableCoordinatesFields"
                  :mask="longitudeMask"
                  :tokens="maskPatterns"
                  :masked="true"
                  class="form-control"
                />
              </b-col>
            </b-row>
          </fieldset>
        </b-col>
      </b-row>
      <b-row
        class="mt-4"
        align-h="end"
      >
        <b-col cols="auto">
          <b-button
            class="mr-2"
            variant="secondary"
            :disabled="isSaving"
            @click="cancel"
          >
            {{ $t('general.cancel') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="isSaving || !areCoordinatesValid"
            @click="confirmSave"
          >
            <s-loader
              v-if="isSaving"
              class="mr-2 d-inline-flex"
            />
            {{ $t('general.conclude') }}
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </app-content>
</template>

<script>
import LoadingMixin from '@/services/loadingMixin';
import installationService from '@/domain/services/installation';
import TimezoneService from '@/domain/services/timezone';
import { GeoCoordinatesEnum } from 'sigma-mappings';
import {
  geoCoordinatesPattern,
  geoCoordinatesMask,
} from '@/services/maskService';

export default {
  name: 'EditInstallation',
  mixins: [LoadingMixin],
  data: () => ({
    installation: {},
    isSaving: false,
    timeZones: [],
  }),
  computed: {
    installationId() {
      return (this.$route.params || {}).installationId;
    },
    latitudeOptions() {
      const range = { min: -90.0000000000000000, max: 90.0000000000000000 };
      return {
        range,
        maxLength: 19,
      };
    },
    longitudeOptions() {
      const range = { min: -180.000000000000000, max: 180.000000000000000 };
      return {
        range,
        maxLength: 20,
      };
    },
    maskPatterns() {
      return geoCoordinatesPattern();
    },
    latitudeMask() {
      return geoCoordinatesMask((this.installation || {}).latitude, GeoCoordinatesEnum.LATITUDE);
    },
    longitudeMask() {
      return geoCoordinatesMask((this.installation || {}).longitude, GeoCoordinatesEnum.LONGITUDE);
    },
    disableCoordinatesFields() {
      return this.isSaving || !this.$hasUserPermission('PER008');
    },
    areCoordinatesValid() {
      if (this.disableCoordinatesFields) {
        return true;
      }

      const isLatitudeInRange = parseFloat(this.installation.latitude)
        >= this.latitudeOptions.range.min
        && parseFloat(this.installation.latitude) <= this.latitudeOptions.range.max;

      const isLongitudeInRange = parseFloat(this.installation.longitude)
        >= this.longitudeOptions.range.min
        && parseFloat(this.installation.longitude) <= this.longitudeOptions.range.max;

      return isLatitudeInRange && isLongitudeInRange;
    },
  },
  created() {
    this.getInstallation();
  },
  methods: {
    setPageName(name) {
      this.$route.meta.pageName = name;
    },
    getInstallation() {
      this.startLoading();
      installationService.getAccessInstallationData(this.installationId).then((response) => {
        const timezone = (response.timezone || {}).id;
        this.installation = { ...response, timezone };
        this.setPageName(response.name);
        this.loadTimeZones();
      }).finally(() => { this.endLoading(); });
    },
    setDefaultTimezoneOnInstallationIfEmpty(defaultTimezoneId) {
      if (!this.installation.timezone) {
        this.installation.timezone = defaultTimezoneId;
      }
    },
    loadTimeZones() {
      TimezoneService.getTimezones().then((response) => {
        this.timeZones = response.timeZones;
        this.setDefaultTimezoneOnInstallationIfEmpty(response.default.id);
      });
    },
    goToList() {
      this.$router.push({ name: 'admin-panel', params: { activeTabName: 'installations' } });
    },
    cancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.goToList();
      });
    },
    confirmSave() {
      this.$confirmMessage({ code: 'MC011' }).accepted(() => {
        this.save();
      });
    },
    save() {
      this.isSaving = true;
      installationService.editAccessInstallationData(
        this.installationId, this.installation,
      ).then(() => {
        this.$pushAlert({ code: 'MS006' });
        this.goToList();
      }).finally(() => { this.isSaving = false; });
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .installation-edit
    &__container
      background-color: var(--color-background-light)
      border: 1px solid $color-gray-medium-highlight
      border-radius: 3px
      padding: 15px 25px
      height: 100%
    &__coordinates-fieldset
      margin-top: 10px
      padding: 20px
      border: 1px solid $color-gray-medium-highlight
      border-radius: 3px
    &__coordinates-legend
      font-size: 1rem
      font-weight: bold
      margin-left: 10px
      padding: 0 5px
      margin-bottom: 0
      width: auto
    .row
      margin: 10px 0
</style>
